import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, type ApiResponse, useAxiosInstance } from 'shared/hooks/utils';

import { addActionNameToMutationResult } from 'shared/hooks/utils/add-action-name-to-mutation-result';
import { z } from 'zod/v4';

const productVariablesSchema = z.object({
  description: z.string().optional().nullable(),
  name: z.string().min(1),
  priceHtva: z.coerce.number(),
  taxCategoryCode: z.string().min(1),
});
export type ProductVariables = z.infer<typeof productVariablesSchema>;

export async function saveProductFn(axiosInstance: Promise<AxiosInstance>, data: ProductVariables) {
  try {
    productVariablesSchema.parse(data);
  } catch (error) {
    console.error(error?.toString());
  }

  const instance = await axiosInstance;
  return instance.post('products', productVariablesSchema.parse(data));
}

export function useSaveProduct() {
  const axiosInstance = useAxiosInstance();
  const client = useQueryClient();

  const mutation = useMutation<ApiResponse, ApiError, ProductVariables>({
    mutationFn: (data: ProductVariables) => saveProductFn(axiosInstance, data),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['products'] });
    },
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, ProductVariables, 'saveProduct'>(
    'saveProduct',
    mutation,
  );
}
